<template>
  <div class="quote-builder-sidebar box">
    <nav class="main-nav">
      <ul>
        <li
          :class="{ active: selectedMenu === MENU_MODES.MANUAL, disabled: !isQuoteEditable }"
          @click="isQuoteEditable && $emit('update:selectedMenu', MENU_MODES.MANUAL)"
        >
          <span class="mso mso-24px mr-2">docs</span>
          <span>Manual Quote</span>
        </li>
        <li
          :class="{ active: selectedMenu === MENU_MODES.TEMPLATES, disabled: !isQuoteEditable }"
          @click="isQuoteEditable && $emit('update:selectedMenu', MENU_MODES.TEMPLATES)"
        >
          <span class="mso mso-24px mr-2">file_copy</span>
          <span>Templates</span>
        </li>
        <li
          :class="{ active: selectedMenu === MENU_MODES.VEHICLE, disabled: !isQuoteEditable }"
          @click="isQuoteEditable && $emit('update:selectedMenu', MENU_MODES.VEHICLE)"
        >
          <span class="mso mso-24px mr-2">no_crash</span>
          <span>Vehicle Sections</span>
        </li>
      </ul>
      <div
        v-show="selectedMenu === MENU_MODES.VEHICLE"
        class="vehicle-sections"
      >
        <hr class="divider my-2">
        <div
          v-if="isShowVehicleSectionContent"
          class="is-flex is-justify-content-space-between is-align-items-center mb-4">
          <span class="has-text-weight-bold">Sections</span>
          <div class="is-flex"
            style="gap: 0.25rem;">
            <button
              class="button is-small"
              style="padding: 0.25em 0.25em; min-width: 0;"
              @click="$emit('modify')"
            >
              <span class="mso mso-24px">car_gear</span>
              <span>Butterfly</span>
            </button>
            <button
              class="button is-small"
              :class="{ 'tooltip': innerSelectedSubSections.length }"
              style="padding: 0.25em; min-width: 0;"
              data-tooltip="Reset"
              @click="$emit('reset')"
              :disabled="!innerSelectedSubSections.length"
            >
              <span class="mso mso-24px">refresh</span>
            </button>
          </div>
        </div>

        <div
          v-show="isShowVehicleSectionContent"
          class="vehicle-group-section">
          <!-- Part Mode Only -->
          <div>
            <div
              v-for="area in computedAreasWithParts"
              :key="area.name"
              class="area-group"
            >
              <div
                :class="['area-header', {
                  'is-area-selected': innerSelectedArea === area.name
                }]"
              >
                <div class="area-content"
                  @click="filterByArea(area)">
                  <div class="area-name">
                    <span class="area-name-text">{{ area.name }}</span>
                  </div>
                  <div class="area-info">
                    <span v-show="innerSelectedArea === area.name"
                      class="mso is-size-6 is-white">keyboard_arrow_right</span>
                  </div>
                </div>
              </div>

              <div class="parts-list">
                <div
                  v-for="part in area.parts"
                  :key="part.partId"
                  :class="['part-item', { 'is-selected': part.isFilter }]"
                >
                  <div class="part-remove">
                    <span
                      class="mso mso-16px remove-icon"
                      @click.stop="removePartSelection(part)"
                      title="Remove part"
                    >close</span>
                  </div>
                  <div class="part-content"
                    @click="togglePartSelection(part, area)">
                    <div class="part-name">{{ part.partName }}</div>
                    <div class="part-info">
                      <span v-show="part.isFilter"
                        class="mso is-size-6">keyboard_arrow_right</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="job-value-section">
        <hr class="divider">
        <div class="has-text-centered small-menu-text">Total Job Value</div>
        <table class="table is-striped is-extra-narrow is-fullwidth small-menu-text">
          <tbody class="no-border">
            <tr v-for="itemType in itemTypes"
              :key="itemType.itemCategoryType">
              <td>{{ itemType.displayName }}</td>
              <td class="has-text-centered">{{ mainSuppQuotePercentages[itemType.itemCategoryType].toFixed(0) }}<span style="font-size: 0.7em; vertical-align: super">%</span></td>
              <td class="has-text-right">{{ $filters.formatCurrency(mainSuppQuoteTotals[itemType.itemCategoryType], $userInfo.locale) }}</td>
            </tr>
          </tbody>
          <tfoot class="no-border">
            <tr>
              <td colspan="2">Total <span class="subtext">(Ex GST)</span></td>
              <td class="has-text-right has-text-success">{{ $filters.formatCurrency(totalExGst, $userInfo.locale) }}</td>
            </tr>
            <tr>
              <td colspan="2">Total <span class="subtext">(Inc GST)</span></td>
              <td class="has-text-right has-text-success">{{ $filters.formatCurrency(totalIncGst, $userInfo.locale) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </nav>
  </div>
</template>

<script>
import { repairAreaGroupingData } from '@/views/quote/components/repairAreaGroupingData.js'
import Utils from '@/utils'
import QuoteService from '@/views/quote/QuoteService'
import _cloneDeep from 'lodash.clonedeep'

export const MENU_MODES = Object.freeze({
  MANUAL: 'manual',
  TEMPLATES: 'templates',
  VEHICLE: 'vehicle'
})

export default {
  name: 'QuoteBuilderSidebar',
  props: {
    value: null, //quote object
    selectedMenu: {
      type: String,
      default: MENU_MODES.MANUAL
    },
    selectedSubSections: {
      type: Array,
      default: () => []
    },
    selectedArea: {
      type: String,
      default: null
    },
    availableItemsType: {
      type: String,
      default: 'PARTS'
    },
    isQuoteEditable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      mainQuote: null,
      supplementaryQuotes: null,
      innerSelectedSubSections: [],
      innerSelectedArea: null
    }
  },
  computed: {
    MENU_MODES() {
      return MENU_MODES
    },
    isShowVehicleSectionContent() {
      return this.availableItemsType === 'PARTS' || this.availableItemsType === 'LABOUR'
    },
    computedAreasWithParts() {
      // Import repair area data
      const allAreas = repairAreaGroupingData
      const predefinedAreas = ['Front', 'Centre', 'Rear', 'Trim/Glass']

      const areaGroups = {}

      predefinedAreas.forEach((areaName, index) => {
        areaGroups[areaName] = {
          parts: []
        }
      })

      if (this.innerSelectedSubSections && this.innerSelectedSubSections.length > 0) {
        this.innerSelectedSubSections.forEach(item => {
          if (item.selected !== true) return

          const areaName = item.area || item.section || 'Other'

          if (predefinedAreas.includes(areaName)) {
            const areaKey = Utils.toCamelCase(areaName)
            const areaData = allAreas[areaKey]
            let partDetails = null

            if (areaData) {
              partDetails = areaData.items.find(p => p.partId === item.partId)
            }

            const part = {
              partId: item.partId,
              partName: partDetails?.partName || item.partName || item.partId,
              area: item.area,
              selected: item.selected === true,
              sortOrder: item.sortOrder || partDetails?.sortOrder || 999,
              originalItem: item,
              isFilter: item.isFilter || false
            }

            areaGroups[areaName].parts.push(part)
          }
        })

        predefinedAreas.forEach(areaName => {
          areaGroups[areaName].parts.sort((a, b) => a.sortOrder - b.sortOrder)
        })
      }

      const result = predefinedAreas.map(areaName => {
        const areaData = areaGroups[areaName]
        return {
          name: areaName,
          parts: areaData.parts
        }
      })

      return result
    },

    itemTypes() {
      const categories = new Set(['RR', 'REP', 'PAINT', 'MECH', 'PART', 'MISC', 'SUBL'])
      const itemCategories = this.$company.itemCategories ?
        this.$company.itemCategories.filter((i) => i.enabled && categories.has(i.itemCategoryType)) : []
      return itemCategories
    },
    mainSuppQuotePercentages() {
      const totals = this.mainSuppQuoteTotals
      const totalExGst = this.totalExGst

      if (totalExGst == 0) {
        return Object.keys(totals).reduce((acc, key) => {
          acc[key] = 0
          return acc
        }, {})
      }
      return Object.keys(totals).reduce((acc, key) => {
        acc[key] = (totals[key] / totalExGst) * 100
        return acc
      }, {})
    },
    totalExGst() {
      return Object.values(this.mainSuppQuoteTotals).reduce((sum, val) => sum + val, 0)
    },
    totalIncGst() {
      if (!this.totalExGst) return 0
      return this.totalExGst + this.totalExGst * (this.value.gstRate / 100)
    },
    isSuppQuote() {
      return (this.value && this.value.subQuoteNo) ? this.value.subQuoteNo > 0 : false
    },
    mainSuppQuoteTotals() {
      const suppQuotes = this.supplementaryQuotes || [] // ensure it's an array
      const mainQuote = this.mainQuote || null

      if (!this.value) {
        return {
          RR: 0,
          REP: 0,
          PAINT: 0,
          MECH: 0,
          PART: 0,
          MISC: 0,
          SUBL: 0
        }
      }

      if (!this.isSuppQuote) {
        // load a main quote
        return {
          RR: this.calculateGrandTotal(
            this.value.rrTotal || 0,
            suppQuotes.map((q) => q.rrTotal || 0)
          ),
          REP: this.calculateGrandTotal(
            this.value.repairTotal || 0,
            suppQuotes.map((q) => q.repairTotal || 0)
          ),
          PAINT: this.calculateGrandTotal(
            this.value.paintTotal || 0,
            suppQuotes.map((q) => q.paintTotal || 0)
          ),
          MECH: this.calculateGrandTotal(
            this.value.mechTotal || 0,
            suppQuotes.map((q) => q.mechTotal || 0)
          ),
          PART: this.calculateGrandTotal(
            this.value.partTotal || 0,
            suppQuotes.map((q) => q.partTotal || 0)
          ),
          MISC: this.calculateGrandTotal(
            this.value.miscTotal || 0,
            suppQuotes.map((q) => q.miscTotal || 0)
          ),
          SUBL: this.calculateGrandTotal(
            this.value.sublTotal || 0,
            suppQuotes.map((q) => q.sublTotal || 0)
          )
        }
      } else {
        // load a supp quote
        if (!mainQuote) {
          // Return a default object while loading
          return { RR: 0, REP: 0, PAINT: 0, MECH: 0, PART: 0, MISC: 0, SUBL: 0 }
        }
        const suppQuotesExcludingCurrent = suppQuotes.filter((quote) => quote.quoteId !== this.value.quoteId)
        return {
          RR: this.calculateGrandTotal(
            mainQuote.rrTotal + this.value.rrTotal,
            suppQuotesExcludingCurrent.map((q) => q.rrTotal || 0)
          ),
          REP: this.calculateGrandTotal(
            mainQuote.repairTotal + this.value.repairTotal,
            suppQuotesExcludingCurrent.map((q) => q.repairTotal || 0)
          ),
          PAINT: this.calculateGrandTotal(
            mainQuote.paintTotal + this.value.paintTotal,
            suppQuotesExcludingCurrent.map((q) => q.paintTotal || 0)
          ),
          MECH: this.calculateGrandTotal(
            mainQuote.mechTotal + this.value.mechTotal,
            suppQuotesExcludingCurrent.map((q) => q.mechTotal || 0)
          ),
          PART: this.calculateGrandTotal(
            mainQuote.partTotal + this.value.partTotal,
            suppQuotesExcludingCurrent.map((q) => q.partTotal || 0)
          ),
          MISC: this.calculateGrandTotal(
            mainQuote.miscTotal + this.value.miscTotal,
            suppQuotesExcludingCurrent.map((q) => q.miscTotal || 0)
          ),
          SUBL: this.calculateGrandTotal(
            mainQuote.sublTotal + this.value.sublTotal,
            suppQuotesExcludingCurrent.map((q) => q.sublTotal || 0)
          )
        }
      }
    }
  },
  watch: {
    selectedMenu: {
      handler(newVal) {
        if (newVal === MENU_MODES.VEHICLE && !this.computedAreasWithParts.length) {
          this.$emit('modify')
        }
      }
    },
    selectedSubSections: {
      handler(newVal) {
        this.reset()
        this.innerSelectedSubSections = _cloneDeep(newVal)
      },
      deep: true,
      immediate: false
    },
    isQuoteEditable: {
      handler(newVal) {
        if (newVal == false) {
          this.$emit('update:selectedMenu', MENU_MODES.MANUAL)
        }
      },
      immediate: true
    }
  },
  async created() {
    this.innerSelectedSubSections = _cloneDeep(this.selectedSubSections)

    await this.getMainQuote()
    await this.getSupplementaryQuoteTotals()
  },
  mounted() {
    this.$emit('update:selectedMenu', MENU_MODES.MANUAL)
  },
  methods: {
    filterByArea(area) {
      if (!area || !area.name) return

      if (this.innerSelectedArea === area.name) {
        this.innerSelectedArea = null
        this.$emit('update:selectedArea', this.innerSelectedArea)
        return
      }

      this.innerSelectedSubSections = this.innerSelectedSubSections.map(subSection => {
        return {
          ...subSection,
          isFilter: false
        }
      })

      this.innerSelectedArea = area.name
      this.$emit('update:selectedArea', this.innerSelectedArea)
      // this.$emit('update:selectedSubSections', this.innerSelectedSubSections)
    },

    togglePartSelection(part, area) {
      if (this.innerSelectedSubSections) {
        this.innerSelectedSubSections = this.innerSelectedSubSections.map(section => {
          return {
            ...section,
            isFilter: section.partId === part.partId && section.area === part.area
          }
        })
      }
      this.innerSelectedArea = null
      this.$emit('update:selectedArea', this.innerSelectedArea)
      this.$emit('update:selectedSubSections', this.innerSelectedSubSections)
    },

    async removePartSelection(part) {
      if (this.innerSelectedSubSections) {
        this.innerSelectedSubSections = this.innerSelectedSubSections.map(section => {
          if (section.partId === part.partId && section.area === part.area) {
            return {
              ...section,
              selected: false,
              isFilter: false
            }
          }
          return section
        })

        const item = this.innerSelectedSubSections.find(section => section.partId === part.partId && section.area === part.area)
        if (item) {
          const data = {
            area: item.area,
            butterflyCode: item.partId,
            butterflyDesc: item.partName,
            isSelected: item.selected,
            sortOrder: item.sortOrder
          }
          await QuoteService.updateQuoteButterflyIsSelected(this.value.quoteId, data)
          this.ensureFilterItemExists()
        }
      }

      this.innerSelectedArea = null
      this.$emit('update:selectedArea', this.innerSelectedArea)
      this.$emit('update:selectedSubSections', this.innerSelectedSubSections)
    },
    ensureFilterItemExists() {
      const hasFilterItem = this.innerSelectedSubSections.some(item => item.isFilter === true)
      if (!hasFilterItem) {
        const firstSelectedItem = this.innerSelectedSubSections.find(item => item.selected === true)
        if (firstSelectedItem) {
          this.$set(firstSelectedItem, 'isFilter', true)
        }
      }
    },
    calculateGrandTotal(mainCategorytotal, supplementaryCategoryTotalsArray) {
      const suppCategoryTotal = supplementaryCategoryTotalsArray.reduce(function (total, suppTotal) {
        return total + suppTotal
      }, 0)
      return mainCategorytotal + suppCategoryTotal
    },
    async getSupplementaryQuoteTotals() {
      if (this.isSuppQuote && this.value.subQuoteNos.length > 1 && this.value.isNew) {
        // get supp quotes when create a new sub quote
        this.supplementaryQuotes = await QuoteService.getSupplementaryQuoteTotals(this.value.subQuoteNos[0].value)
        return
      }
      if (this.value && this.value.quoteId) {
        this.supplementaryQuotes = await QuoteService.getSupplementaryQuoteTotals(this.value.quoteId)
      }
      else {
        this.supplementaryQuotes = []
      }
    },
    async getMainQuote() {
      if (!this.isSuppQuote) return
      this.mainQuote = await QuoteService.getMainQuoteEntity(`${this.value.prefix}${this.value.quoteNo}${this.value.suffix}`)
    },
    reset() {
      this.innerSelectedSubSections = []
      this.innerSelectedArea = null
    }
  }
}
</script>

<style scoped>
.quote-builder-sidebar {
  width: 250px;
  min-height: 78vh;
  display: flex;
  flex-direction: column;
}
.main-nav {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.main-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.main-nav li {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 0.25rem;
  font-weight: 500;
  /* color: #222; */
}
.main-nav li.active, .main-nav li:hover {
  background: #1976d2;
  color: #fff;
}
.main-nav li.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.main-nav li.disabled:hover {
  background: transparent;
  color: inherit;
}
.main-nav .icon {
  margin-right: 0.75rem;
  font-size: 1.2em;
}
.vehicle-group {
  margin-bottom: 1.25rem;
}
.group-title {
  font-weight: 600;
  color: #888;
  font-size: 0.95em;
  margin-bottom: 0.25rem;
}
.vehicle-group ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.vehicle-group li {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9em;
  background: #f7fafd;
  margin-bottom: 0.15rem;
  cursor: pointer;
}
.vehicle-group li:hover {
  background: #e8f4fd;
}
.vehicle-group li.is-selected {
  background: #e3f0ff;
  border-left: 3px solid #1976d2;
}
.vehicle-group .remove-x {
  color: #888;
  margin-right: 0.5em;
  cursor: pointer;
  font-weight: bold;
  width: 16px;
  text-align: center;
}
.vehicle-group .add-plus {
  color: #4caf50;
  margin-right: 0.5em;
  cursor: pointer;
  font-weight: bold;
  width: 16px;
  text-align: center;
}
.vehicle-group .arrow {
  margin-left: auto;
  color: #bbb;
  font-size: 1.1em;
}

.vehicle-group-section {
  max-height: calc(100vh - 600px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.area-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.area-name {
  font-weight: 600;
  font-size: 0.95em;
  display: flex;
  align-items: center;
}

.area-name-text {
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.no-border tr,
.no-border td,
.no-border th {
  border: none;
}
.is-extra-narrow td,
.is-extra-narrow th {
  padding: 0.1em 0.5em;
}

.subtext {
  font-size: 10px;
}

.small-menu-text {
  font-size: 14px;
}

.divider {
  background-color: hsl(0, 0%, 90%) !important;
}

.job-value-section {
  margin-top: auto;
}

/* Area group styles for part mode */
.area-group {
  margin-bottom: 0.75rem;
}
.area-header {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  background: var(--background-color);
  border: 1px solid var(--border-color);
}

.area-header:hover {
  background: var(--background-hover-color);
  border: 1px solid var(--border-hover-color);
}
.area-header.is-area-selected {
  background: #1976d2;
  border-color: #1976d2;
}

.area-header.is-area-selected .area-name-text {
  color: #fff;
  font-weight: 700;
}
/* Parts list styles */
.parts-list {
  margin-top: 0.25rem;
  margin-left: 0.25rem;
  border-left: 2px solid #e3eefa;
  padding-left: 0.5rem;
}

.part-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem;
  border-radius: 4px;
  margin-bottom: 0.25rem;
  font-size: 0.85em;
  /* color: #555; */
  /* background: #fafbfc; */
  background: var(--background-color);
  border: 1px solid var(--border-color);
  /* border: 1px solid #e8eef5; */
}

.part-item:hover {
  /* color: #1976d2; */
  /* background: #f0f5fa; */
  /* border-color: #1976d2; */
  background: var(--background-hover-color);
  border: 1px solid var(--border-hover-color);
}

.part-item.is-selected {
  background: #1976d2;
  color: #fff;
  border-color: #1976d2;
  font-weight: 500;
}

.part-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  cursor: pointer;
}

.part-name {
  font-weight: 500;
}

.part-info {
  display: flex;
  align-items: center;
}

.area-info {
  display: flex;
  align-items: center;
  color: #fff;
}

.part-remove {
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
}

.remove-icon {
  font-size: 1rem;
  color: #999;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.remove-icon:hover {
  color: #e74c3c;
  background: rgba(231, 76, 60, 0.1);
}

.part-item.is-selected .remove-icon {
  color: rgba(255, 255, 255, 0.8);
}

.part-item.is-selected .remove-icon:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
}

</style>